import React, { Fragment, useContext, useState } from 'react';
import { isNil } from 'lodash';
import { renderPerformanceBar } from 'legacy-src/components/assessment/Results/utility';
import { performanceTierStacks } from 'legacy-src/utility/progressHelpers';
import { assignmentScrambledOrder } from 'legacy-src/queries';
import StudentsDropdown from 'legacy/src/components/assignment/Controls/StudentsDropdown/StudentsDropdown';
import Header from 'chameleon/ui-stack/ui/page/header';
import Button from 'chameleon/ui-stack/ui/base/button';
import ScoredIcon from 'chameleon/ui-stack/ui/assessments/scored-icon';
import { BreadcrumbContext } from 'chameleon/ui-stack/ui/basic/breadcrumbs';
import InformationalPopover from 'chameleon/ui-stack/ui/popovers/informational-popover';
import { Icon } from 'chameleon/legacy/packages/ft-cb';
import BaseIcon from 'chameleon/ui-stack/ui/base/icon';
import { StartDueLabels } from './utils/utils';
import SwitchField from '../SwitchField';
import './styles.scss';
import usePerformanceTiers from 'chameleon/src/hooks/usePerformanceTiers';

const StudentResultsHeader = ({
  isStudentResponseAvailable,
  assignment,
  student,
  studentScore,
  onButtonClick,
  showPerformanceAndTime,
  submissionData,
  currentStudentId,
  params,
  isStudent = false,
  router,
  results,
  studentsBySection,
  client,
  hasScramblePerStudent,
  meQuery: { me },
  toggleQuestionsOrder,
  assignment: { scrambleQuestionsPerStudent, scoreDisplay },
  reviewModeFlag,
  immediateFeedbackFlag,
}) => {
  const { performanceTiers } = usePerformanceTiers(params.subject);

  const [isOrderScrambled, setIsOrderScrambled] = useState(
    sessionStorage.getItem(`scrambled-order-${assignment.id}`) !== 'false',
  );
  const viewingQuestionDetail = !isNil(params.questionId);
  const subtitle = viewingQuestionDetail
    ? 'Question Details'
    : isStudent
      ? 'RESULTS OVERVIEW'
      : 'STUDENT RESULTS';
  const buttonText = isStudent
    ? 'View Your Responses'
    : 'View Student Responses';
  const studentSubmission =
    submissionData.find(
      (submission) => submission.user_id.toString() === currentStudentId,
    ) || {};
  const studentSubmissionFromProgress = isStudent ? assignment.progress : {};
  const teacherAndStudentScored =
    studentSubmission.status === 'teacher_and_student_scored' ? 1 : 0;
  const studentScored = studentSubmission.status === 'student_scored' ? 1 : 0;
  const teacherScored = studentSubmission.status === 'scored' ? 1 : 0;
  const { dispatch, ACTIONS } = useContext(BreadcrumbContext);

  const onStudentChange = (option) => {
    const redirectUrl = `/${params.subject}/assessments/results/${params.assignmentId}/students/${option.value}/questions`;
    router.replace(redirectUrl);
    dispatch({
      type: ACTIONS.updateSingleBreadcrumb,
      payload: { breadcrumb: 'Student Results', to: redirectUrl },
    });
  };

  /**
   * An ISA will have assignment.students, otherwise we need to check for the students
   * belonging to the section.
   */
  const students =
    assignment.students || studentsBySection[assignment.section_id] || [];
  const stacks = performanceTierStacks(
    null,
    results,
    performanceTiers,
    students,
  );

  const handleQuestionsOrderToggle = async (value) => {
    const parsedValue = value === 'on';

    setIsOrderScrambled(parsedValue);
    sessionStorage.setItem(`scrambled-order-${assignment.id}`, parsedValue);

    const studentId = me.isStudent ? me.initId : null;

    const { data } = await client.query({
      query: assignmentScrambledOrder,
      variables: {
        assignmentId: params.assignmentId,
        scrambledStudentId: parsedValue ? studentId : null,
      },
    });

    const rawOrder = data.assignment.questions.map((question) => question.id);

    toggleQuestionsOrder(rawOrder);
  };
  // hasScramblePerStudent is FF and scrambleQuestionsPerStudent is if the assignment was scrambled
  const showToggleOrder =
    hasScramblePerStudent && isStudent && scrambleQuestionsPerStudent;

  const partialScoreDisplay =
    reviewModeFlag && scoreDisplay === 'partial' && isStudent;

  return (
    <Fragment>
      <Header subtitle={subtitle} title={assignment && assignment.title}>
        <div className="header_container">
          <div className="header_date justify-center flex mobile:flex-col mt-8 gap-12 text-gray-700 gap-14 items-center">
            {!isStudent && (
              <StudentsDropdown
                students={assignment.students}
                studentId={student.id}
                studentDisplayName={`${student.firstName} ${student.lastName}`}
                onChange={onStudentChange}
                stacks={stacks}
              />
            )}
            <StartDueLabels
              assignment={assignment}
              studentId={params?.studentId}
            />
            {immediateFeedbackFlag && assignment.hasImmediateFeedback && (
              <span className="flex items-center immediate-feedback-icon">
                <BaseIcon name="immediate_feedback" />{' '}
                <label className="pl-4">Immediate Feedback ON</label>
              </span>
            )}
            {isStudentResponseAvailable && !partialScoreDisplay && (
              <Button isPrimary onClick={onButtonClick}>
                {/* view (all) student response(s) */}
                {buttonText}
              </Button>
            )}
          </div>
          <div
            aria-live="polite"
            className={`scramble-toggle w-fit flex mobile:ml-0 h-min mt-8 bg-blue-100 items-center
            ${
              showToggleOrder ? 'py-0.8 px-4.6' : 'p-0'
            } rounded-full font-medium gap-4.6`}
          >
            {showToggleOrder && (
              <>
                <div className="flex mr-4.6 items-center">
                  <span className="mr-0.8">View questions in order taken</span>
                  <InformationalPopover
                    className="text-left"
                    size="medium"
                    message="Other students in your class saw this assignment’s questions in a different order.
                    Choose whether to view questions in the order you took them,
                    or in a shared default order that your teacher and classmates can also view."
                    accessibility={{
                      'aria-label': 'information icon',
                    }}
                    trigger={
                      <Icon
                        name="circle_info"
                        size="1.2em"
                        className="fill-current text-gray-600"
                      />
                    }
                  />
                </div>
                <SwitchField
                  onChange={handleQuestionsOrderToggle}
                  value={isOrderScrambled ? 'on' : 'off'}
                />
              </>
            )}
          </div>
        </div>
      </Header>
      <div className="w-full flex items-center gap-10">
        {showPerformanceAndTime &&
          renderPerformanceBar({
            studentScore,
            progress: assignment.progress,
            studentSubmission,
          })}
        {![teacherAndStudentScored, studentScored, teacherScored].every(
          (el) => el === 0,
        ) && (
          <ScoredIcon
            teacherAndStudentScored={teacherAndStudentScored}
            studentScored={studentScored}
            teacherScored={teacherScored}
            {...studentSubmissionFromProgress}
          />
        )}
      </div>
      {partialScoreDisplay && (
        <div className="w-full flex items-center gap-2 p-2 h-16 bg-blue-100 border border-blue-600 mt-9 rounded-md">
          <Icon
            name="filled_circle_info"
            size="1em"
            className="fill-blue-700 text-blue-600"
          />
          Some information is hidden by the teacher.
        </div>
      )}
    </Fragment>
  );
};

export default StudentResultsHeader;
